import React, { useEffect, useState, useRef, useContext } from 'react'
import axios from 'axios'
import DataTable from 'react-data-table-component'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlass, faUpload, faCircleXmark, faCommentDollar, faFilePdf, faPlusCircle, faEye, faMoneyBill, faFileCsv } from '@fortawesome/free-solid-svg-icons'
import { faOpenid } from '@fortawesome/free-brands-svg-icons'
import CustomLoader from '../layouts/CustomLoader'
import { Link, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import { DateTime } from 'luxon'
import CustomerForm from './adminForms/CustomerForm'
import PartnerForm from './adminForms/PartnerForm'
import MarketingForm from './adminForms/MarketingForm'
import AuthContext from '../../middlewares/AuthContext'
import { useTranslation } from 'react-i18next'
import usePaginationComponentOptions from '../layouts/paginationComponentOptions'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

const BRAND = '#6eb446'

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
}

const Admin: React.FC = () => {
  const { usertypes }  = useParams<{ usertypes: string }>()
  const { getHeaders } = useContext(AuthContext) as AuthCtx
  const { t, i18n }    = useTranslation()
  const paginationComponentOptions = usePaginationComponentOptions()

  const btnInvoices          = useRef<HTMLButtonElement>(null)
  const btnInvoicesStores    = useRef<HTMLButtonElement>(null)
  const btnInvoicesStoresCls = useRef<HTMLButtonElement>(null)

  const [data, setData]           = useState<any[]>([])
  const [loading, setLoading]     = useState(false)
  const [totalRows, setTotalRows] = useState(0)
  const [perPage, setPerPage]     = useState(10)
  const [invoices, setInvoices]   = useState<any>(null)
  const [searchOpen, setSearchOpen] = useState(false)
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false)

  // customer search
  const [_stripe, set_stripe]         = useState('')
  const [_payeremail, set_payeremail] = useState('')
  const [_package, set_package]       = useState('')
  // partner search
  const [partner, setPartner] = useState('')
  // marketing search
  const [marketing, setMarketing] = useState('')

  const buildQuery = () => {
    if (usertypes === 'customers')  return `&stripe=${_stripe}&payeremail=${_payeremail}&package=${_package}`
    if (usertypes === 'partners')   return `&stripe=${_stripe}&partner=${partner}`
    if (usertypes === 'marketings') return `&stripe=${_stripe}&payeremail=${_payeremail}&marketing=${marketing}`
    return ''
  }

  const fetchPayments = async (page: number, perPageVal = perPage) => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const res     = await axios.get<{ message: any[]; total: number }>(
        `${url}payments/admin/${usertypes}?page=${page}&per_page=${perPageVal}${buildQuery()}`,
        { headers }
      )
      setData(res.data.message)
      setTotalRows(res.data.total)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setLoading(false)
    }
  }

  const handlePageChange    = (page: number) => fetchPayments(page)
  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage)
    fetchPayments(page, newPerPage)
  }

  // ---- helpers ----

  const sumTotal = (items: any[]) =>
    items.reduce((s: number, e: any) => s + parseFloat(e.total_amount), 0).toFixed(2)

  const downloadPdf = (base64: string, name: string) => {
    const link    = document.createElement('a')
    link.href     = `data:application/pdf;base64,${base64}`
    link.download = name
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const downloadBlob = (blob: Blob, filename: string) => {
    const blobUrl = window.URL.createObjectURL(blob)
    const link    = document.createElement('a')
    link.href     = blobUrl
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(blobUrl)
  }

  // ---- API actions ----

  const getInvoicePartner = async (params: number) => {
    try {
      const headers = { ...(await getHeaders()), 'Content-Type': 'application/pdf' }
      const res     = await axios.get(`${url}payments/admin/${usertypes}/invoice/${params}`, { headers })
      downloadPdf(res.data.message, res.data.name)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    }
  }

  const getInvoicePartnerPayment = async (params: string) => {
    try {
      const headers = await getHeaders()
      const res     = await axios.post(`${url}partners/partner-pdf`, { pi: params }, { headers })
      downloadPdf(res.data.message, res.data.name)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    }
  }

  const getCSVPartner = async (params: string) => {
    const date = DateTime.now().toFormat('yyyy_LLL_dd_HH_mm_ss')
    try {
      const headers = await getHeaders()
      const res     = await axios.post(`${url}partners/partner-csv`, { pi: params }, { headers })
      downloadBlob(new Blob([res.data]), `report-partner-range-${date}.csv`)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    }
  }

  const getInvoices = (pi: any, pay = 'marketing') => {
    setInvoices(pi)
    if (pay === 'marketing') btnInvoices.current?.click()
    else btnInvoicesStores.current?.click()
  }

  const getInvoiceMarketings = async (params: string) => {
    if (!invoices) return
    try {
      const headers = await getHeaders()
      const res     = await axios.post(`${url}marketings/get-invoices-sub-pdf`, { pi: params, markpaymentId: invoices.id }, { headers })
      downloadPdf(res.data.message, res.data.name)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    }
  }

  const getInvoiceStores = async (params: any) => {
    btnInvoicesStores.current?.click()
    try {
      const headers = await getHeaders()
      const res     = await axios.post(`${url}stores/store-pdf`, { pi: params.invoice_store }, { headers })
      downloadPdf(res.data.message, res.data.name)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setTimeout(() => btnInvoicesStoresCls.current?.click(), 500)
    }
  }

  const getUrlStore = async (params: string) => {
    Swal.fire({ html: `<div class="text-center"><div class="spinner-border" role="status"></div></div>`, showConfirmButton: false })
    try {
      const headers = await getHeaders()
      const res     = await axios.get(`${url}stores/get-url/${params}`, { headers })
      Swal.fire({ html: `<code>${res.data.message}</code>` })
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    }
  }

  const viewPaymentConfirm = async (params: string) => {
    try {
      const headers = await getHeaders()
      const res = await axios({ url: `${url}payments/view-confirm-prepayment/${params}`, method: 'get', headers, responseType: 'blob' })
      const disposition = res.headers['content-disposition']
      const match       = disposition?.match(/filename="(.+)"/)
      const fileName    = match ? match[1] : 'File-download'
      downloadBlob(new Blob([res.data]), fileName)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error', text: err?.response?.data?.message })
    }
  }

  const notRefund = () => {
    Swal.fire({ icon: 'error', title: t('payments.admin.refound_title'), text: t('payments.admin.refound_txt') })
  }

  const generateExternalPayment = async (cs_stripe_id: string) => {
    const result = await Swal.fire({
      title: t('payments.admin.generate_external_title'),
      text: t('payments.admin.generate_external_text'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('payments.admin.confirm_externally'),
      customClass: { confirmButton: 'btn btn-warning', cancelButton: 'btn btn-primary mx-2' },
      buttonsStyling: false,
    })
    if (!result.isConfirmed) return
    try {
      const headers = await getHeaders()
      const res     = await axios.get(`${url}partners/generate-external-payment/${cs_stripe_id}`, { headers })
      fetchPayments(1)
      setResetPaginationToggle(p => !p)
      Swal.fire({ icon: 'success', title: res.data.response, text: res.data.message })
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error', text: err?.response?.data?.message })
    }
  }

  const externallyPaid = async (params: string) => {
    const result = await Swal.fire({
      title: t('payments.admin.externally_title_1'),
      text: t('payments.admin.externally_text_1'),
      input: 'select',
      showCancelButton: true,
      inputOptions: { confirm: 'Confirm payment', error: 'Error on transfer' },
      confirmButtonText: 'Send',
      customClass: { confirmButton: 'btn btn-outline-danger', cancelButton: 'btn btn-primary mx-2' },
      buttonsStyling: false,
    })
    if (!result.isConfirmed) return
    try {
      const headers = await getHeaders()
      const res     = await axios.get(`${url}partners/externally-paid/${params}/${result.value}`, { headers })
      fetchPayments(1)
      setResetPaginationToggle(p => !p)
      Swal.fire({ icon: 'success', title: res.data.response, text: res.data.message })
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error', text: err?.response?.data?.message })
    }
  }

  const refund = async (params: string) => {
    const first = await Swal.fire({
      title: t('payments.admin.refound_title_2'),
      text: t('payments.admin.refound_text_2'),
      showCancelButton: true,
      confirmButtonText: 'Refund',
      customClass: { confirmButton: 'btn btn-outline-danger', cancelButton: 'btn btn-primary mx-2' },
      buttonsStyling: false,
    })
    if (!first.isConfirmed) return
    const { value: reason } = await Swal.fire({
      title: 'Select a reason to refund',
      html: `<select id="swal-refund" class="form-select mb-3"><option value="duplicate">Duplicate</option><option value="fraudulent">Fraudulent</option><option value="requested_by_customer">Requested by customer</option></select>`,
      focusConfirm: false,
      preConfirm: () => (document.getElementById('swal-refund') as HTMLSelectElement).value,
    })
    if (!reason) return
    try {
      const headers = await getHeaders()
      const res     = await axios.post(`${url}payments/refund`, { pi_stripe_id: params, reason }, { headers })
      Swal.fire({ icon: 'success', title: 'Success', text: res.data.response })
      const payment = res.data.message
      setData(prev => prev.map(item => item.pi_stripe_id === payment.pi_stripe_id ? { ...item, payment_status: payment.payment_status } : item))
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.response })
    }
  }

  useEffect(() => { fetchPayments(1) }, [i18n.language])

  // ---- COLUMN SETS ----

  const customerColumns = [
    {
      maxWidth: '20%',
      name: t('payments.admin.stripe_payment'),
      cell: (row: any) => (
        <ul className="ps-0 my-2 small">
          <li className="list-group-item pb-1 mb-1 border-bottom"><b>Checkout ID: </b>{row.cs_stripe_id}</li>
          <li className="list-group-item pb-1 mb-1"><b>Pi_ID: </b>{row.pi_stripe_id}</li>
        </ul>
      ),
    },
    {
      maxWidth: '25%',
      name: t('payments.admin.stripe_data'),
      cell: (row: any) => {
        const d = new Date(row.createdAt)
        return (
          <div className="w-100 text-start small">
            <div className="mb-1"><b>{t('payments.admin.payer_email')}: </b>{row.Payeremail?.email}</div>
            <div className="mb-1"><b>{t('payments.admin.date_purchase')}: </b>{d.toISOString().slice(0, 10)} {d.toISOString().slice(11, 19)}</div>
            <div className="mb-1"><b>{t('payments.admin.amount_total')}: </b>${sumTotal(row.Itempayments)} {row.Itempayments[0]?.currency}</div>
            <div className={`badge ${row.payment_status === 'paid' ? 'text-bg-success' : 'text-bg-warning'}`}>
              <span className="text-uppercase fw-bold">{row.payment_status}</span>
            </div>
          </div>
        )
      },
    },
    {
      maxWidth: '20%',
      name: t('payments.admin.product'),
      cell: (row: any) => (
        <ul className="ps-0 my-2 small">
          {row.Packages?.map((e: any, i: number) => (
            <li key={i} className="list-group-item pb-1 mb-1 border-bottom">
              <b>{i + 1}.-</b> {e?.Plans[0]?.name || '—'} / {e?.Plans[0]?.Product?.name || '—'}{' '}
              ${parseFloat(row.Itempayments[0]?.currency === 'mxn' ? e?.Plans[0]?.price_mxn : e?.Plans[0]?.price).toFixed(2)} {row.Itempayments[0]?.currency}
            </li>
          ))}
        </ul>
      ),
    },
    {
      maxWidth: '25%',
      name: t('payments.admin.package'),
      cell: (row: any) => (
        <ul className="ps-0 my-2 small">
          {row.Packages?.map((e: any, i: number) => {
            let days = 0, months = 0, years = 0
            if (parseInt(e.TimeId) === 1) years = e.expiry_time
            else if (parseInt(e.TimeId) === 2) months = e.expiry_time
            else days = e.expiry_time
            const expiry = DateTime.fromISO(e.createdAt).plus({ days, months, years }).toISODate()
            return (
              <li key={i} className="list-group-item pb-1 mb-1 border-bottom">
                <b>{i + 1}.-</b>{' '}
                <Link to={`/packages/show/${e.serial_number}`} target="_blank" rel="noopener noreferrer">{e.serial_number}</Link>{' '}
                | {e.Status?.name} | {expiry}
              </li>
            )
          })}
        </ul>
      ),
    },
    {
      minWidth: '150px',
      name: t('payments.admin.actions'),
      cell: (row: any) => {
        const ex    = DateTime.fromJSDate(new Date(row.createdAt))
        const isOld = DateTime.now().diff(ex, ['days']).days >= 31 || row.payment_status === 'refunded'
        return (
          <div className="dropdown mb-2">
            <button className="btn btn-sm btn-primary dropdown-toggle" data-bs-toggle="dropdown" style={{ width: 120 }}>
              {t('payments.admin.options')}
            </button>
            <ul className="dropdown-menu">
              <li>
                <button
                  className={`dropdown-item fw-bold ${isOld ? 'text-muted opacity-50' : 'text-warning'}`}
                  onClick={() => isOld ? notRefund() : refund(row.pi_stripe_id)}
                >
                  <FontAwesomeIcon icon={faCommentDollar} className="me-2" />{t('payments.admin.refund')}
                </button>
              </li>
            </ul>
          </div>
        )
      },
    },
  ]

  const partnerColumns = [
    {
      name: t('payments.admin.stripe_data'),
      cell: (row: any) => (
        <ul className="ps-0 my-2 small">
          <li className="list-group-item pb-1 mb-1 border-bottom"><b>Checkout ID: </b>{row.cs_stripe_id}</li>
          <li className="list-group-item pb-1 mb-1"><b>Payment intent ID: </b>{row.pi_stripe_id}</li>
        </ul>
      ),
    },
    {
      maxWidth: '120px',
      name: t('payments.admin.status'),
      cell: (row: any) => {
        const s = row.payment_status
        if (s === 'paid' || s === 'externally paid')
          return <div className="badge text-bg-primary text-wrap"><p style={{ fontSize: 12 }} className="text-capitalize fw-bold mb-0 text-light">{s === 'externally paid' ? t('payments.partner.externally') : t('payments.partner.paid')}</p></div>
        if (s === 'pending' || s === 'pending transfer')
          return <div className="badge text-bg-warning text-wrap"><p style={{ fontSize: 12 }} className="text-capitalize fw-bold mb-0 text-light">{s}</p></div>
        return <div className="badge text-bg-light text-wrap"><p style={{ fontSize: 12 }} className="text-capitalize fw-bold mb-0 text-dark">{s}</p></div>
      },
    },
    {
      maxWidth: '120px',
      name: t('payments.admin.quantity_license'),
      cell: (row: any) => <span>{row.licenses_quantity}</span>,
    },
    {
      maxWidth: '20px',
      name: t('user_type.partner'),
      cell: (row: any) => (
        <div className="text-start w-100 small">
          <p className="text-capitalize mb-0">{row.Partner?.name}</p>
          {row?.User?.prepay && <p className="text-capitalize fw-bold mb-0">Prepay</p>}
        </div>
      ),
    },
    {
      maxWidth: '20px',
      name: t('payments.admin.amount_total'),
      cell: (row: any) => <span>${parseFloat(row.total_amount).toFixed(2)}</span>,
    },
    {
      maxWidth: '220px',
      name: t('Fechas'),
      cell: (row: any) => {
        const sd = new Date(row.start_date), ed = new Date(row.end_date), cd = new Date(row.createdAt)
        return (
          <div className="small">
            <p className="mb-0 fw-bold">Periodo de suscripciones</p>
            <p className="mb-1">{sd.toISOString().slice(0, 10)} - {ed.toISOString().slice(0, 10)}</p>
            <p className="mb-0 fw-bold">Fecha de emisión</p>
            <p className="mb-0">{cd.toISOString().slice(0, 10)} {cd.toISOString().slice(11, 19)}</p>
          </div>
        )
      },
    },
    {
      name: t('payments.admin.actions'),
      cell: (row: any) => {
        const pcs = row.payment_status === 'payment confirm send' && row?.User?.prepay
        const pe  = row.payment_status === 'externally paid' && row?.User?.prepay
        return (
          <div className="dropdown mb-2">
            <button className="btn btn-sm btn-primary dropdown-toggle" data-bs-toggle="dropdown">
              {t('payments.admin.options')}
            </button>
            <ul className="dropdown-menu">
              <li>
                <button className="dropdown-item fw-bold" onClick={() => getInvoicePartner(row.id)}>
                  <FontAwesomeIcon icon={faFilePdf} className="me-2" />Recibo
                </button>
              </li>
              <li>
                <button className="dropdown-item fw-bold" onClick={() => getCSVPartner(row.cs_stripe_id)}>
                  <FontAwesomeIcon icon={faFileCsv} className="me-2" />Reporte CSV
                </button>
              </li>
              {(pe || pcs) && (
                <li>
                  <button className="dropdown-item fw-bold" onClick={() => viewPaymentConfirm(row.cs_stripe_id)}>
                    <FontAwesomeIcon icon={faEye} className="me-2" />Payment confirm
                  </button>
                </li>
              )}
              {pe && (
                <li>
                  <button className="dropdown-item fw-bold" onClick={() => getInvoicePartnerPayment(row.pi_stripe_id)}>
                    <FontAwesomeIcon icon={faMoneyBill} className="me-2" />Payment
                  </button>
                </li>
              )}
              {pcs && (
                <li>
                  <button className="dropdown-item fw-bold text-warning" onClick={() => externallyPaid(row.cs_stripe_id)}>
                    <FontAwesomeIcon icon={faCommentDollar} className="me-2" />{t('payments.partner.externally')}
                  </button>
                </li>
              )}
              {row.payment_status !== 'paid' && row.payment_status !== 'externally paid' && (
                <li>
                  <button className="dropdown-item fw-bold text-success" onClick={() => generateExternalPayment(row.cs_stripe_id)}>
                    <FontAwesomeIcon icon={faMoneyBill} className="me-2" />{t('payments.admin.generate_external')}
                  </button>
                </li>
              )}
            </ul>
          </div>
        )
      },
    },
  ]

  const marketingColumns = [
    {
      maxWidth: '5%',
      name: 'Marketing',
      cell: (row: any) => (
        <div className="small">
          <p className="text-capitalize mb-0">{row.Marketing?.name}</p>
          <p className="mb-0">Invoice: {row.Marketing?.invoice_email}</p>
        </div>
      ),
    },
    {
      maxWidth: '20%',
      name: t('payments.admin.stripe_data'),
      cell: (row: any) => (
        <ul className="ps-0 my-2 small">
          <li className="list-group-item pb-1 mb-1 border-bottom"><b>Checkout ID: </b>{row.cs_stripe_id}</li>
          <li className="list-group-item pb-1 mb-1"><b>Pay ID: </b>{row.pi_stripe_id}</li>
        </ul>
      ),
    },
    {
      maxWidth: '10%',
      name: t('payments.admin.status'),
      cell: (row: any) => (
        <>
          <div className={`badge ${row.status_suscription === 'active' ? 'text-bg-primary' : 'text-bg-warning'}`}>
            <p style={{ fontSize: 12 }} className="text-capitalize fw-bold mb-0 text-light">{row.status_suscription}</p>
          </div>
          <p className="mt-1 mb-0 small">{t('payments.admin.count')}: <b>{row.count_subscription}</b></p>
        </>
      ),
    },
    {
      maxWidth: '10%',
      name: t('payments.admin.amount_total'),
      cell: (row: any) => <span className="small">{row.total_amount !== null ? `$${parseFloat(row.total_amount).toFixed(2)}` : '—'}</span>,
    },
    {
      maxWidth: '15%',
      name: t('payments.admin.expire_at'),
      cell: (row: any) => (
        <div className="small">
          <span><b>{t('payments.admin.created_at')}:</b> {DateTime.fromISO(row.createdAt).toFormat('yyyy-MM-dd')}</span>
          <br />
          <span><b>{t('payments.admin.expire_at')}:</b> {DateTime.fromISO(row.expireAt).toFormat('yyyy-MM-dd')}</span>
        </div>
      ),
    },
    {
      maxWidth: '20%',
      name: 'Region',
      cell: (row: any) => (
        <div className="text-start w-100 small">
          <span><b>{t('payments.admin.country')}:</b> {row?.Markprice?.country}</span>
          <p className="mb-0"><b>{t('payments.admin.states')}:</b></p>
          {row?.Markprice?.States?.map((e: any, i: number) => <p key={i} className="m-0">{e.name}</p>)}
        </div>
      ),
    },
    {
      maxWidth: '20%',
      name: t('payments.admin.actions'),
      cell: (row: any) => row.pi_stripe_id === null ? null : (
        <div className="dropdown mb-2">
          <button className="btn btn-sm btn-bd-primary dropdown-toggle" data-bs-toggle="dropdown" style={{ width: 120 }}>
            {t('payments.admin.options')}
          </button>
          <ul className="dropdown-menu">
            <li>
              <button className="dropdown-item fw-bold" onClick={() => getInvoices(row)}>
                <FontAwesomeIcon icon={faFilePdf} className="me-2" />Invoices
              </button>
            </li>
            <li>
              <Link className="dropdown-item fw-bold" to={`/marketings/advertising/${row.pi_stripe_id}`}>
                <FontAwesomeIcon icon={faUpload} className="me-2" />{t('payments.admin.upload_banners')}
              </Link>
            </li>
          </ul>
        </div>
      ),
    },
  ]

  const storeColumns = [
    {
      maxWidth: '15%',
      name: t('payments.admin.store'),
      cell: (row: any) => (
        <div className="small">
          <p className="text-capitalize mb-0"><b>{row?.Store?.name || '—'}</b></p>
          <p className="mb-0">{row?.Store?.phone || '—'}</p>
          <p className="mb-0">{row?.Store?.email || '—'}</p>
          <p className="mb-0">{row?.Store?.link || '—'}</p>
        </div>
      ),
    },
    {
      maxWidth: '5%',
      name: `${t('payments.admin.name')} & ${t('payments.admin.description')}`,
      cell: (row: any) => (
        <div className="small">
          <p className="text-capitalize mb-0">{row.name}</p>
          <p className="mb-0">{row.description}</p>
        </div>
      ),
    },
    {
      maxWidth: '20%',
      name: t('payments.admin.stripe_payment'),
      cell: (row: any) => (
        <ul className="ps-0 my-2 small">
          <li className="list-group-item pb-1 mb-1 border-bottom"><b>Checkout ID: </b>{row.cs_checkout_id}</li>
          <li className="list-group-item pb-1 mb-1"><b>Payment intent ID: </b>{row.pi_stripe_id}</li>
        </ul>
      ),
    },
    {
      maxWidth: '5%',
      name: t('payments.admin.status'),
      cell: (row: any) => (
        <div className={`badge ${row.payment_status === 'active' || row.payment_status === 'paid' ? 'text-bg-primary' : 'text-bg-warning'}`}>
          <p style={{ fontSize: 12 }} className="text-capitalize fw-bold mb-0 text-light">{row.payment_status}</p>
        </div>
      ),
    },
    {
      maxWidth: '10%',
      name: t('payments.admin.price'),
      cell: (row: any) => <span className="small">{row.price !== null ? `$${parseFloat(row.price).toFixed(2)}` : '—'}</span>,
    },
    {
      maxWidth: '15%',
      name: t('payments.admin.expire_at'),
      cell: (row: any) => {
        const expireAt = row?.expireAt ? DateTime.fromISO(row.expireAt).toFormat('yyyy-MM-dd') : '—'
        return (
          <div className="small">
            <span><b>{t('payments.admin.created_at')}:</b> {DateTime.fromISO(row.createdAt).toFormat('yyyy-MM-dd')}</span>
            <br />
            <span><b>{t('payments.admin.expire_at')}:</b> {expireAt}</span>
          </div>
        )
      },
    },
    {
      maxWidth: '10%',
      name: t('payments.admin.region_due_date'),
      cell: (row: any) => (
        <div className="text-start w-100 small">
          <span><b>Country:</b> {row?.country} - {row?.country_iso}</span>
          <p className="mb-0"><b>Expiry:</b> {row.expiry_time} {row.Time?.name}</p>
        </div>
      ),
    },
    {
      maxWidth: '20%',
      name: t('payments.admin.actions'),
      cell: (row: any) => row.pi_stripe_id === null ? null : (
        <div className="dropdown mb-2">
          <button className="btn btn-sm btn-bd-primary dropdown-toggle" data-bs-toggle="dropdown" style={{ width: 120 }}>
            {t('payments.admin.options')}
          </button>
          <ul className="dropdown-menu">
            {row?.invoice_store !== null && (
              <li>
                <button className="dropdown-item fw-bold" onClick={() => getInvoiceStores(row)}>
                  <FontAwesomeIcon icon={faFilePdf} className="me-2" />Invoice
                </button>
              </li>
            )}
            {row?.payment_status === 'open' && (
              <li>
                <button className="dropdown-item fw-bold" onClick={() => getUrlStore(row.cs_checkout_id)}>
                  <FontAwesomeIcon icon={faOpenid} className="me-2" />{t('payments.admin.get_url')}
                </button>
              </li>
            )}
          </ul>
        </div>
      ),
    },
  ]

  const getColumns = () => {
    switch (usertypes) {
      case 'customers':  return customerColumns
      case 'partners':   return partnerColumns
      case 'marketings': return marketingColumns
      case 'stores':     return storeColumns
      default:           return []
    }
  }

  return (
    <>
      {/* Marketing invoices modal */}
      <button type="button" className="d-none" data-bs-toggle="modal" data-bs-target="#adminInvoices" ref={btnInvoices} />
      <div className="modal fade" id="adminInvoices" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Download invoices</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" />
            </div>
            <div className="modal-body">
              <ul className="list-group list-group-flush">
                {invoices?.invoices?.map((e: string, i: number) => (
                  <li key={i} className="list-group-item">
                    <button className="btn btn-link p-0 text-decoration-none" onClick={() => getInvoiceMarketings(e)}>{e}</button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            </div>
          </div>
        </div>
      </div>

      {/* Stores loading modal */}
      <button type="button" className="d-none" data-bs-toggle="modal" data-bs-target="#adminStoresModal" ref={btnInvoicesStores} />
      <div className="modal fade" id="adminStoresModal" data-bs-backdrop="static" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered modal-sm">
          <div className="modal-content" style={{ borderRadius: 16, border: 'none' }}>
            <button type="button" className="d-none" data-bs-dismiss="modal" ref={btnInvoicesStoresCls} />
            <div className="modal-body text-center py-4">
              <div className="spinner-border" role="status" style={{ color: BRAND }} />
              <p className="mt-2 mb-0 fw-bold" style={{ fontSize: '0.9rem' }}>Generating file…</p>
            </div>
          </div>
        </div>
      </div>

      <div className="row align-items-start">
        <div className="col-sm-4 col-md-2 mb-3 my-1">
          <span className="fw-bold">{t(`payments.admin.${usertypes}`)}</span>
          {usertypes === 'partners' && <p className="mb-0 small">Pospayment</p>}
          {usertypes === 'stores' && (
            <Link to="/stores/create/payment" className="btn btn-sm btn-shadow btn-bd-primary mt-1">
              <FontAwesomeIcon icon={faPlusCircle} className="me-2" />{t('payments.admin.create_pay')}
            </Link>
          )}
        </div>

        <div className="col-sm-8 col-md-10 mb-3">
          <div style={{ borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', backgroundColor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <button
              type="button"
              onClick={() => setSearchOpen(p => !p)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.25rem', background: searchOpen ? '#f8fafc' : '#fff', border: 'none', borderBottom: searchOpen ? '1px solid #e2e8f0' : 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem', color: '#475569' }}
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} />
              <span>{t('utilities.search')}</span>
              <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#94a3b8', transform: searchOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
            </button>
            {searchOpen && (
              <div style={{ padding: '1.25rem' }}>
                {usertypes === 'customers' && (
                  <CustomerForm
                    _stripe={_stripe} _payeremail={_payeremail} _package={_package}
                    set_stripe={set_stripe} set_payeremail={set_payeremail} set_package={set_package}
                    setLoading={setLoading} usertypes={usertypes} perPage={perPage}
                    setData={setData} setTotalRows={setTotalRows} getHeaders={getHeaders}
                  />
                )}
                {usertypes === 'partners' && (
                  <PartnerForm
                    _stripe={_stripe} partner={partner}
                    set_stripe={set_stripe} setpartner={setPartner}
                    setLoading={setLoading} usertypes={usertypes} perPage={perPage}
                    setData={setData} setTotalRows={setTotalRows} getHeaders={getHeaders}
                  />
                )}
                {usertypes === 'marketings' && (
                  <MarketingForm
                    _stripe={_stripe} set_stripe={set_stripe}
                    _payeremail={_payeremail} set_payeremail={set_payeremail}
                    marketing={marketing} setmarketing={setMarketing}
                    setLoading={setLoading} usertypes={usertypes} perPage={perPage}
                    setData={setData} setTotalRows={setTotalRows} getHeaders={getHeaders}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        <div className="col-md-12">
          <div className="card card-shadow">
            <DataTable
              columns={getColumns()}
              data={data}
              dense
              fixedHeader
              fixedHeaderScrollHeight="500px"
              progressPending={loading}
              paginationResetDefaultPage={resetPaginationToggle}
              pagination
              paginationPerPage={perPage}
              paginationServer
              paginationTotalRows={totalRows}
              onChangeRowsPerPage={handlePerRowsChange}
              onChangePage={handlePageChange}
              paginationComponentOptions={paginationComponentOptions}
              paginationRowsPerPageOptions={[10, 20, 50, 100, 200, 500]}
              progressComponent={<CustomLoader />}
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default Admin
